import { ProductGroup } from '@app/core/models/client';
import { MessageClass } from '@app/core/models/message';
import {
  Message as MessageComponent,
  StyleProvider,
  defaultBrandingData,
  BrandingData,
  DisclaimerContainer,
} from '@relaynetwork/design-system';
import {
  MediaAsset,
  buildFeedMessageStructure,
} from '@relaynetwork/rn-node-feed-api-types';
import { ExpirationUnit } from '@relaynetwork/rn-node-feed-api-types/dist/routes/messages';
import React, { useMemo, useState, useEffect } from 'react';
import { i18nLocal } from '@relaynetwork/design-system';

type WirePreviewProps = {
  message?: MessageClass;
  brandingColor: string;
  disclaimerConfig?: BrandingData['disclaimer'];
  isDestinationMessageDetail?: boolean;
  previewMode?: 'expanded' | 'collapsed';
  components?: any[];
  productGroup?: ProductGroup;
  clientId?: string;
  getMediaAssetForMessage: (assetId: string) => Promise<MediaAsset>;
  language?: string;
  fdic_enabled?: boolean;
  showFeedViewDisclaimer?: boolean;
};

function WirePreview({
  message,
  brandingColor,
  disclaimerConfig,
  isDestinationMessageDetail = false,
  previewMode,
  components,
  productGroup,
  clientId,
  language,
  getMediaAssetForMessage,
  fdic_enabled,
  showFeedViewDisclaimer = false,
}: WirePreviewProps) {
  const [renderedMessage, setRenderedMessage] = useState(null);

  useEffect(() => {
    const messageExpiration = message.parent
      ? components.find((component) => message.parent === component.name)
          ?.message_expiration
      : {
          unit:
            message.message_expiration?.unit === 'days'
              ? ExpirationUnit.Day
              : ExpirationUnit.Week,
          amount: message.message_expiration?.amount || null,
          type: message.message_expiration?.type || null,
        };
    buildFeedMessageStructure(
      {clientId,fdic_enabled},
      components,
      message.name,
      (_, assetId) => {
        return getMediaAssetForMessage(assetId);
      },
      productGroup,
      language,
      productGroup?.consent.in_wire_upgrade.ts_cs,
      messageExpiration,
    )
      .then((message) => {
        setRenderedMessage(message);
      })
      .catch(console.error);
  }, [
    message,
    components,
    productGroup,
    clientId,
    brandingColor,
    disclaimerConfig,
  ]);

  const feedViewDisclaimerText = disclaimerConfig?.text ?? '';

  const brandingData = useMemo(
    () => ({
      ...defaultBrandingData,
      color: brandingColor,
      disclaimer: showFeedViewDisclaimer
        ? {
            feedMenuEnabled: false,
            feedViewEnabled: false,
            messageDetailsEnabled: false,
            messageLevelEnabled: false,
            text: '',
          }
        : disclaimerConfig,
    }),
    [brandingColor],
  );
  useEffect(() => {
    if (productGroup?.i18n?.es) {
      i18nLocal.addResourceBundle('es', 'common', productGroup.i18n.es);
    } else {
      i18nLocal.init();
    }
    if (language) {
      i18nLocal.changeLanguage(language ? language : 'en');
    } else {
      i18nLocal.changeLanguage('en');
    }
  }, [language, productGroup]);
  useEffect(() => {
    return () => {
      i18nLocal.init();
      i18nLocal.changeLanguage('en');
    };
  }, []);

  return (
    renderedMessage && (
      <StyleProvider brandingData={brandingData}>
        <MessageComponent
          message={renderedMessage}
          isPreview
          previewMode={previewMode}
        />
        {isDestinationMessageDetail &&
          brandingData.disclaimer.messageDetailsEnabled && (
            <DisclaimerContainer
              content={brandingData.disclaimer.text ?? ''}
              expandMaxHeight={previewMode === 'expanded'}
            />
          )}
        {showFeedViewDisclaimer && feedViewDisclaimerText.trim() !== '' && (
          <div style={{ position: 'sticky', bottom: 0, zIndex: 1 }}>
            <DisclaimerContainer
              content={feedViewDisclaimerText}
              isDismissible
              expandMaxHeight={previewMode === 'expanded'}
            />
          </div>
        )}
      </StyleProvider>
    )
  );
}

export default WirePreview;
